import styled from "styled-components";

export type ProjectView = "list" | "gallery";

interface ProjectViewProps {
  view: ProjectView;
}

interface ProjectImageProps extends ProjectViewProps {
  background?: string;
}

export const ProjectListSectionContainer = styled.div`
  width: 100%;
  display: block;
  padding-top: 48px;
`;

export const ProjectSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 36px;
  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
  }
`;

export const ProjectSectionTitle = styled.h1`
  margin: 0px;
  color: var(--heading);
  font-size: 30px;
  line-height: 38px;
  font-weight: 500;
`;

export const ProjectViewControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  @media (max-width: 768px) {
    margin: 0px;
  }
`;

export const ProjectViewButton = styled.button`
  position: relative;
  padding: 3px 0px;
  border: none;
  color: var(--muted);
  background: transparent;
  cursor: pointer;
  &:after {
    content: "";
    position: absolute;
    right: 100%;
    bottom: 0;
    left: 0;
    height: 1px;
    background-color: var(--text);
    transition: right 0.2s ease;
  }
  &[aria-pressed="true"] {
    color: var(--heading);
    &:after {
      right: 0;
    }
  }
`;

export const ProjectCompanyContainer = styled.div<ProjectViewProps>`
  display: ${({ view }) => (view === "list" ? "grid" : "flex")};
  grid-template-columns: ${({ view }) =>
    view === "list" ? "112px minmax(0, 1fr)" : undefined};
  gap: ${({ view }) => (view === "list" ? "24px" : "0px")};
  flex-direction: column;
  padding-top: ${({ view }) => (view === "list" ? "18px" : "0px")};
  border-top: ${({ view }) =>
    view === "list" ? "1px solid var(--line)" : "none"};
  margin-bottom: 48px;
  @media (max-width: 768px) {
    display: flex;
    gap: ${({ view }) => (view === "list" ? "24px" : "0px")};
    max-width: 320px;
    margin: 64px auto 0px;
  }
`;

export const ProjectCompanyHeader = styled.div<ProjectViewProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  margin-bottom: 0px;
`;

export const ProjectCompanyName = styled.label<ProjectViewProps>`
  margin-bottom: ${({ view }) => (view === "list" ? "5px" : "8px")};
  font-size: 18px;
  font-weight: 450;
  white-space: nowrap;
`;

export const ProjectCompanyTimeline = styled.label<ProjectViewProps>`
  margin-bottom: ${({ view }) => (view === "list" ? "0px" : "24px")};
  color: ${({ view }) => (view === "list" ? "var(--muted)" : "inherit")};
  font-size: ${({ view }) => (view === "list" ? "14px" : "16px")};
  font-weight: regular;
`;

export const ProjectCardList = styled.div<ProjectViewProps>`
  display: ${({ view }) => (view === "list" ? "flex" : "grid")};
  flex-direction: ${({ view }) => (view === "list" ? "column" : undefined)};
  grid-template-columns: ${({ view }) =>
    view === "gallery" ? "repeat(2, minmax(0, 1fr))" : undefined};
  gap: ${({ view }) => (view === "list" ? "24px" : "64px")};
  font-size: 16px;
  line-height: 20px;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ view }) => (view === "list" ? "28px" : "42px")};
  }
`;

export const ProjectCard = styled.article<ProjectViewProps>`
  display: ${({ view }) => (view === "list" ? "grid" : "flex")};
  grid-template-columns: ${({ view }) =>
    view === "list" ? "minmax(0, 1fr) 104px" : undefined};
  gap: ${({ view }) => (view === "list" ? "26px" : "0px")};
  align-items: ${({ view }) => (view === "list" ? "start" : undefined)};
  flex-direction: column;
  width: 100%;
  padding: 0px;
  border-bottom: none;
  cursor: pointer;
  @media (max-width: 768px) {
    grid-template-columns: ${({ view }) =>
      view === "list" ? "minmax(0, 1fr) 88px" : undefined};
    gap: ${({ view }) => (view === "list" ? "16px" : "0px")};
    width: 320px;
  }
`;

export const ProjectCardContent = styled.div<ProjectViewProps>`
  min-width: 0;
  margin-top: ${({ view }) => (view === "gallery" ? "18px" : "0px")};
`;

export const ProjectCardImage = styled.div<ProjectImageProps>`
  position: relative;
  width: ${({ view }) => (view === "list" ? "104px" : "100%")};
  height: ${({ view }) => (view === "list" ? "104px" : "auto")};
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 2px;
  background: ${({ background }) => background || "var(--surface)"};

  img {
    display: block;
    width: 100%;
    height: 100%;
    transition: transform 0.28s ease;
  }

  ${ProjectCard}:hover & {
    img { filter: saturate(1.04); }
  }
  @media (max-width: 768px) {
    width: ${({ view }) => (view === "list" ? "88px" : "320px")};
    height: ${({ view }) => (view === "list" ? "88px" : "320px")};
  }
`;

export const ProjectCardTitle = styled.h3<ProjectViewProps>`
  margin: 0px 0px 8px;
  color: var(--text);
  font-size: ${({ view }) => (view === "list" ? "21px" : "17px")};
  line-height: ${({ view }) => (view === "list" ? "27px" : "23px")};
  font-weight: 450;
  @media (max-width: 768px) {
    font-size: ${({ view }) => (view === "list" ? "20px" : "17px")};
    line-height: ${({ view }) => (view === "list" ? "26px" : "23px")};
  }
`;

export const ProjectCardDescription = styled.p`
  margin: 0px;
  color: var(--subtle);
  font-size: 14px;
  line-height: 21px;
`;
